import { useQuery } from '@tanstack/react-query'
import { Button, Card, Empty, Flex, Skeleton, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  type AuditLogList,
  type OrganizationsAuditLogsListData,
  organizationsAuditLogsListOptions
} from 'src/api/main'
import { DateTimeCell, SeverityDot } from 'src/components'
import {
  getAuditEventCode,
  humanizeAuditCode,
  normalizeAuditSeverity,
  toAuditAcceptLanguage
} from 'src/utils'

const { Text } = Typography

const RECENT_LIMIT = 5

// `ordering` is accepted by the endpoint but not yet in the generated query
// type (same schema gap UserAuditTab works around); add it so the most recent
// events come first.
type AuditListQuery = NonNullable<OrganizationsAuditLogsListData['query']> & {
  ordering?: string
}

type Props = {
  workspaceId: number
  userId: number
  onSeeAll?: () => void
}

const eventLabel = (event: AuditLogList): string =>
  event.description?.trim() || humanizeAuditCode(getAuditEventCode(event))

export const UserRecentActivityCard = ({
  workspaceId,
  userId,
  onSeeAll
}: Props) => {
  const { t, i18n } = useTranslation()
  const acceptLanguage = toAuditAcceptLanguage(
    i18n.resolvedLanguage ?? i18n.language
  )
  const enabled = workspaceId > 0 && userId > 0

  const query: AuditListQuery = {
    actor_id: String(userId),
    limit: RECENT_LIMIT,
    offset: 0,
    ordering: '-timestamp'
  }

  const { data, isLoading, isError } = useQuery({
    ...organizationsAuditLogsListOptions({
      path: { organization_id: workspaceId },
      query,
      headers: { 'Accept-Language': acceptLanguage }
    }),
    enabled
  })

  const events = data?.results ?? []

  return (
    <Card
      size="small"
      styles={{
        body: { padding: 12 },
        header: { paddingInline: 12 }
      }}
      title={
        <Flex align="center" justify="space-between">
          <Text strong style={{ fontSize: 13 }}>
            {t('users.detail.recentActivity.title')}
          </Text>
          <Button
            size="small"
            type="text"
            disabled={!onSeeAll}
            onClick={onSeeAll}
            style={{ fontSize: 11 }}
          >
            {t('users.detail.recentActivity.seeAll')}
          </Button>
        </Flex>
      }
    >
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : isError || events.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          styles={{ image: { height: 40 } }}
          description={
            <Text type="secondary" style={{ fontSize: 12 }}>
              {isError
                ? t('users.detail.recentActivity.error')
                : t('users.detail.recentActivity.empty')}
            </Text>
          }
          style={{ margin: 0 }}
        />
      ) : (
        <Flex vertical gap={10}>
          {events.map((event) => (
            <Flex key={event.id} align="center" gap={8}>
              <SeverityDot severity={normalizeAuditSeverity(event.severity)} />
              <Flex vertical style={{ minWidth: 0, flex: 1, lineHeight: 1.3 }}>
                <Text
                  style={{ display: 'block', fontSize: 12.5, width: '100%' }}
                  ellipsis={{ tooltip: eventLabel(event) }}
                >
                  {eventLabel(event)}
                </Text>
                <DateTimeCell
                  value={event.timestamp}
                  variant="relative"
                  style={{
                    fontSize: 11,
                    color: 'var(--ant-color-text-tertiary)'
                  }}
                />
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </Card>
  )
}
